import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  alpha,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import SyncProblemIcon from '@mui/icons-material/SyncProblem';
import BoltIcon from '@mui/icons-material/Bolt';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SaveIcon from '@mui/icons-material/Save';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

import { PageHeader, ResponsiveTable, StatsCard, EmptyState } from '../components/common';
import type { Column } from '../components/common/ResponsiveTable';
import { toPersianDigits } from '../components/common/JalaliDatePicker';
import { formatDate } from '../utils/dateUtils';
import snapppayAdminService, {
  SNAPP_ERROR_CODES,
  SNAPP_STATUS_COLORS,
  SNAPP_STATUS_LABELS,
  type SnappPayConnectionTest,
  type SnappPayEligibleTest,
  type SnappPayLog,
  type SnappPayOrderDetail,
  type SnappPayOrderRow,
  type SnappPayOverview,
} from '../services/snapppay.service';
import RefundPreviewSteps, { RefundPreviewData } from '../components/common/RefundPreviewSteps';
import { adminRefundPreviewService } from '../services/digipay.service';

// ---------------------------------------------------------------- helpers

const SNAPP_BLUE = '#008EFA';
const fmtToman = (n?: number | null): string =>
  n === undefined || n === null || Number.isNaN(Number(n)) ? '—' : `${Math.round(Number(n)).toLocaleString('fa-IR')} تومان`;
const fa = (v: any) => toPersianDigits(String(v ?? ''));

const StatusChip: React.FC<{ status?: string | null; size?: 'small' | 'medium' }> = ({ status, size = 'small' }) => {
  const s = status || 'UNKNOWN';
  const color = SNAPP_STATUS_COLORS[s] || SNAPP_STATUS_COLORS.UNKNOWN;
  return (
    <Chip
      size={size}
      label={SNAPP_STATUS_LABELS[s] || s}
      sx={{ bgcolor: alpha(color, 0.12), color, fontWeight: 700, fontSize: 11.5, maxWidth: 260, '& .MuiChip-label': { whiteSpace: 'normal', lineHeight: 1.4, py: 0.4 } }}
    />
  );
};

const Mono: React.FC<{ children?: React.ReactNode; copy?: string }> = ({ children, copy }) => (
  <Box component="span" sx={{ direction: 'ltr', unicodeBidi: 'plaintext', fontFamily: 'monospace', fontSize: 12, wordBreak: 'break-all', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
    {children}
    {copy && (
      <Tooltip title="کپی">
        <IconButton size="small" onClick={(e) => { e.stopPropagation(); navigator.clipboard?.writeText(copy); }} sx={{ p: 0.25 }}>
          <ContentCopyIcon sx={{ fontSize: 13 }} />
        </IconButton>
      </Tooltip>
    )}
  </Box>
);

const Row: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.6, borderBottom: '1px dashed', borderColor: 'divider', fontSize: 13 }}>
    <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>{label}</Typography>
    <Box sx={{ textAlign: 'left', fontWeight: 600, fontSize: 13, minWidth: 0 }}>{value}</Box>
  </Box>
);

const SnappLogo: React.FC<{ size?: number }> = ({ size = 28 }) => (
  <img src="/snapppay-logo.svg" alt="اسنپ‌پی" width={size} height={size} style={{ borderRadius: Math.round(size * 0.25), display: 'block' }} />
);

const LOG_ACTION_LABELS: Record<string, string> = { request: 'درخواست توکن', callback: 'کال‌بک', verify: 'verify / settle', refund: 'استرداد (cancel/update/revert)', inquiry: 'استعلام' };
const LOG_STATUS_COLORS: Record<string, string> = { success: '#16A34A', failed: '#DC2626', pending: '#D97706' };

// ── نمایش خوانای جزئیات لاگ (به‌جای JSON خام) ──
const FIELD_LABELS: Record<string, string> = {
  transactionId: 'شناسهٔ تراکنش', paymentToken: 'توکن پرداخت', orderNumber: 'شمارهٔ سفارش', orderId: 'شناسهٔ سفارش',
  amount: 'مبلغ', baseAmount: 'مبلغ سفارش (کالا)', surchargeAmount: 'کارمزد خرید اقساطی', surchargePercent: 'درصد کارمزد', surchargeMode: 'مبنای کارمزد',
  payableAmount: 'مبلغ قابل پرداخت', previousAmount: 'مبلغ قبلی', newAmount: 'مبلغ جدید', requestedRefund: 'مبلغ استرداد درخواستی', refunded: 'مبلغ مستردشده',
  items: 'تعداد اقلام', discountToman: 'تخفیف', discountAmount: 'تخفیف', returnURL: 'آدرس بازگشت', paymentPageUrl: 'صفحهٔ پرداخت اسنپ‌پی',
  snapppayStatus: 'وضعیت در اسنپ‌پی', outcome: 'نتیجه', step: 'مرحله', source: 'منبع عملیات', adminId: 'ادمین', reason: 'دلیل', state: 'وضعیت کال‌بک',
  settledAt: 'زمان نهایی‌سازی', reverted: 'revert انجام شد', context: 'زمینه', httpStatus: 'کد HTTP', errorCode: 'کد خطا', successful: 'موفق',
  response: 'پاسخ اسنپ‌پی', errorData: 'خطای اسنپ‌پی', message: 'پیام', data: 'داده', eligible: 'مجاز به خرید اقساطی', title_message: 'عنوان', description: 'توضیح',
  status: 'وضعیت', code: 'کد', mode: 'حالت', mobile: 'موبایل', cartList: 'سبد خرید', cartItems: 'اقلام سبد', cartId: 'شناسهٔ سبد', totalAmount: 'جمع سبد',
  shippingAmount: 'هزینهٔ ارسال', taxAmount: 'مالیات', isShipmentIncluded: 'ارسال در قیمت لحاظ شده', isTaxIncluded: 'مالیات در قیمت لحاظ شده',
  externalSourceAmount: 'مبلغ از منبع خارجی', forcedPaymentMethodTypes: 'روش‌های پرداخت اجباری', commissionType: 'نوع کمیسیون', category: 'دسته', count: 'تعداد', name: 'نام', id: 'شناسه',
  'transaction-status': 'وضعیت تراکنش', errors: 'خطاها', mapping: 'نگاشت', used: 'استفاده‌شده', deepLink: 'دیپ‌لینک اپ', createdAt: 'زمان ایجاد', verifiedAt: 'زمان تأیید',
  feeEstimated: 'کارمزد برآوردی', gatewayFee: 'کارمزد درگاه', ip: 'IP', userAgent: 'مرورگر',
};
const OUTCOME_LABELS: Record<string, string> = { verified: 'تأیید شد', failed: 'ناموفق', indeterminate: 'نامشخص', cancel: 'لغو کامل', update: 'کاهش مبلغ', noop: 'بدون تغییر' };
const AMOUNT_KEYS = /amount|toman|refund(ed)?$|^fee|price/i;

function formatLogValue(key: string, v: any, rial: boolean): React.ReactNode {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'boolean') return v ? 'بله' : 'خیر';
  if (key === 'snapppayStatus' || key === 'transaction-status' || key === 'status') {
    const up = String(v).toUpperCase();
    return SNAPP_STATUS_LABELS[up] ? SNAPP_STATUS_LABELS[up] : String(v);
  }
  if (key === 'outcome' || key === 'mode') return OUTCOME_LABELS[String(v)] || String(v);
  if (typeof v === 'number') {
    if (AMOUNT_KEYS.test(key) && !/percent|count|items|code|status|id$/i.test(key)) return `${v.toLocaleString('fa-IR')} ${rial ? 'ریال' : 'تومان'}`;
    if (/percent/i.test(key)) return `${v.toLocaleString('fa-IR')}٪`;
    return v.toLocaleString('fa-IR');
  }
  if (typeof v === 'string') {
    if (/^\d{4}-\d{2}-\d{2}T/.test(v)) return formatDate(v);
    if (/^https?:\/\//.test(v) || /^[A-Za-z0-9_\-]{16,}$/.test(v)) return <Mono copy={v}>{v}</Mono>;
    return v;
  }
  return null;
}

const LogKV: React.FC<{ obj: any; rial?: boolean; depth?: number }> = ({ obj, rial = false, depth = 0 }) => {
  if (obj === null || obj === undefined) return <Typography variant="body2" color="text.secondary">—</Typography>;
  if (Array.isArray(obj)) {
    return (
      <Stack spacing={0.75} sx={{ pr: depth ? 1.5 : 0 }}>
        {obj.map((item, i) => (
          <Box key={i} sx={{ p: 1, borderRadius: 1, border: '1px dashed', borderColor: 'divider' }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>مورد {toPersianDigits(String(i + 1))}</Typography>
            {typeof item === 'object' ? <LogKV obj={item} rial={rial} depth={depth + 1} /> : <Typography variant="body2">{String(item)}</Typography>}
          </Box>
        ))}
      </Stack>
    );
  }
  if (typeof obj !== 'object') return <Typography variant="body2">{String(obj)}</Typography>;
  const entries = Object.entries(obj).filter(([, v]) => v !== undefined);
  if (entries.length === 0) return <Typography variant="body2" color="text.secondary">—</Typography>;
  return (
    <Box sx={{ pr: depth ? 1.5 : 0, borderRight: depth ? '2px solid' : 'none', borderColor: 'divider' }}>
      {entries.map(([k, v]) => {
        const label = FIELD_LABELS[k] || k;
        const simple = formatLogValue(k, v, rial);
        if (simple !== null) {
          return (
            <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5, borderBottom: '1px dashed', borderColor: 'divider', fontSize: 12.5 }}>
              <Typography variant="body2" color="text.secondary" sx={{ flexShrink: 0 }}>{label}</Typography>
              <Box sx={{ textAlign: 'left', fontWeight: 600, fontSize: 12.5, minWidth: 0, wordBreak: 'break-word' }}>{simple}</Box>
            </Box>
          );
        }
        return (
          <Box key={k} sx={{ py: 0.75 }}>
            <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>{label}</Typography>
            <LogKV obj={v} rial={rial} depth={depth + 1} />
          </Box>
        );
      })}
    </Box>
  );
};

const LogDetails: React.FC<{ log: SnappPayLog }> = ({ log }) => {
  const sections: Array<{ title: string; obj: any; rial?: boolean; hint?: string }> = [
    { title: 'خلاصه', obj: { status: log.status === 'success' ? 'موفق' : log.status === 'failed' ? 'ناموفق' : 'در انتظار', message: log.message, amount: log.amount, transactionId: log.refId, paymentToken: log.authority, code: log.code, ...(log.error ? { errors: log.error } : {}) } },
    { title: 'اطلاعات عملیات', obj: log.meta },
    { title: 'درخواست ارسالی به اسنپ‌پی', obj: log.requestBody },
    { title: 'پاسخ اسنپ‌پی', obj: log.responseBody, rial: true, hint: 'مبالغ پاسخ اسنپ‌پی به ریال هستند' },
    { title: 'دادهٔ بازگشتی از درگاه (کال‌بک)', obj: log.callbackQuery, rial: true, hint: 'مبلغ کال‌بک به ریال است' },
  ].filter((x) => x.obj && (typeof x.obj !== 'object' || Object.keys(x.obj).length > 0));
  return (
    <Grid container spacing={1.5}>
      {sections.map((sec) => (
        <Grid item xs={12} md={6} key={sec.title}>
          <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>{sec.title}</Typography>
            {sec.hint && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>{sec.hint}</Typography>}
            <LogKV obj={sec.obj} rial={sec.rial} />
          </Paper>
        </Grid>
      ))}
    </Grid>
  );
};

// ---------------------------------------------------------------- page

const SnappPayCenter: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = Number(searchParams.get('tab') || (searchParams.get('search') ? 1 : 0)) || 0;
  const [tab, setTab] = useState<number>(initialTab);

  // ── overview ──
  const [overview, setOverview] = useState<SnappPayOverview | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);
  const loadOverview = useCallback(async () => {
    setOverviewLoading(true);
    try {
      setOverview(await snapppayAdminService.overview());
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || e?.message || 'خطا در دریافت نمای کلی اسنپ‌پی', { variant: 'error' });
    } finally {
      setOverviewLoading(false);
    }
  }, [enqueueSnackbar]);
  useEffect(() => { loadOverview(); }, [loadOverview]);

  // ── orders ──
  const [orders, setOrders] = useState<SnappPayOrderRow[]>([]);
  const [ordersTotal, setOrdersTotal] = useState(0);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [statusFilter, setStatusFilter] = useState<string>(searchParams.get('status') || '');
  const [search, setSearch] = useState<string>(searchParams.get('search') || '');
  const [searchInput, setSearchInput] = useState<string>(searchParams.get('search') || '');
  const [onlySyncError, setOnlySyncError] = useState(false);

  const loadOrders = useCallback(async () => {
    setOrdersLoading(true);
    try {
      const r = await snapppayAdminService.listOrders({ page: page + 1, limit: rowsPerPage, status: statusFilter, search, syncError: onlySyncError });
      setOrders(r?.docs || []);
      setOrdersTotal(r?.total || 0);
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || e?.message || 'خطا در دریافت سفارش‌های اسنپ‌پی', { variant: 'error' });
    } finally {
      setOrdersLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, search, onlySyncError, enqueueSnackbar]);
  useEffect(() => { if (tab === 1) loadOrders(); }, [tab, loadOrders]);

  // ── detail dialog ──
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detail, setDetail] = useState<SnappPayOrderDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const loadDetail = useCallback(async (id: string) => {
    setDetailLoading(true);
    try {
      setDetail(await snapppayAdminService.orderDetail(id));
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || e?.message || 'خطا در دریافت جزئیات سفارش', { variant: 'error' });
      setDetailId(null);
    } finally {
      setDetailLoading(false);
    }
  }, [enqueueSnackbar]);
  useEffect(() => { if (detailId) loadDetail(detailId); else setDetail(null); }, [detailId, loadDetail]);

  const [confirm, setConfirm] = useState<{ kind: 'cancel' | 'revert' | 'update' | 'settle'; amount?: string; includeSurcharge?: boolean } | null>(null);
  // «دقیقاً چه اتفاقی می‌افتد» — پیش‌نمایش سرور برای cancel/update (با تغییر مبلغ/سوئیچ کارمزد دوباره محاسبه می‌شود)
  const [confirmPreview, setConfirmPreview] = useState<RefundPreviewData | null>(null);
  const [confirmPreviewLoading, setConfirmPreviewLoading] = useState(false);
  const [confirmPreviewError, setConfirmPreviewError] = useState<string | null>(null);
  const confirmIncludeFee = confirm?.includeSurcharge ?? (overview?.refundIncludesSurcharge ?? true);
  useEffect(() => {
    if (!confirm || !detailId || !detail || (confirm.kind !== 'cancel' && confirm.kind !== 'update')) { setConfirmPreview(null); setConfirmPreviewError(null); return; }
    let cancelled = false;
    const o: any = detail.order;
    const paidNow = Math.round(Number(o?.paidAmount ?? o?.payableAmount ?? o?.totalAmount) || 0);
    const newAmount = confirm.kind === 'update' && confirm.amount && confirm.amount.trim() !== '' ? Number(confirm.amount.replace(/[^\d]/g, '')) : undefined;
    // update = کاهش مبلغ ⇒ مبلغ استرداد = فعلی − جدید؛ cancel = کل
    const refundAmt = confirm.kind === 'update' && newAmount !== undefined && newAmount > 0 && newAmount < paidNow ? paidNow - newAmount : undefined;
    const t = setTimeout(async () => {
      setConfirmPreviewLoading(true); setConfirmPreviewError(null);
      try {
        const p = await adminRefundPreviewService.order(detailId, { amount: refundAmt, includeSurcharge: confirmIncludeFee });
        if (!cancelled) setConfirmPreview(p);
      } catch (e: any) {
        if (!cancelled) setConfirmPreviewError(e?.response?.data?.message || e?.message || 'خطا');
      } finally {
        if (!cancelled) setConfirmPreviewLoading(false);
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [confirm, detailId, detail, confirmIncludeFee]);
  const runAction = async (kind: 'status' | 'settle' | 'cancel' | 'update' | 'revert', amount?: number, includeSurcharge?: boolean) => {
    if (!detailId) return;
    setBusy(kind);
    try {
      let msg = 'انجام شد';
      if (kind === 'status') {
        const r: any = await snapppayAdminService.orderStatus(detailId);
        msg = `وضعیت در اسنپ‌پی: ${SNAPP_STATUS_LABELS[r?.status] || r?.status || '—'}${r?.amountToman ? ` — مبلغ ${fmtToman(r.amountToman)}` : ''}`;
        enqueueSnackbar(msg, { variant: 'info' });
      } else {
        const r: any = kind === 'settle' ? await snapppayAdminService.orderSettle(detailId)
          : kind === 'cancel' ? await snapppayAdminService.orderCancel(detailId, 'لغو کامل توسط ادمین از مرکز اسنپ‌پی', includeSurcharge)
          : kind === 'update' ? await snapppayAdminService.orderUpdate(detailId, amount, 'کاهش مبلغ توسط ادمین از مرکز اسنپ‌پی', includeSurcharge)
          : await snapppayAdminService.orderRevert(detailId, 'revert توسط ادمین از مرکز اسنپ‌پی');
        msg = r?.message || (r?.data && r.data.message) || 'انجام شد';
        enqueueSnackbar(msg, { variant: 'success' });
      }
      await loadDetail(detailId);
      loadOrders();
      loadOverview();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || e?.message || 'خطا در ارتباط با اسنپ‌پی', { variant: 'error' });
    } finally {
      setBusy(null);
      setConfirm(null);
    }
  };

  // ── logs ──
  const [logs, setLogs] = useState<SnappPayLog[]>([]);
  const [logsTotal, setLogsTotal] = useState(0);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logPage, setLogPage] = useState(0);
  const [logRows, setLogRows] = useState(25);
  const [logAction, setLogAction] = useState('');
  const [logStatus, setLogStatus] = useState('');
  const [logSearch, setLogSearch] = useState('');
  const [logSearchInput, setLogSearchInput] = useState('');
  const [expandedLog, setExpandedLog] = useState<string | null>(null);
  const loadLogs = useCallback(async () => {
    setLogsLoading(true);
    try {
      const r = await snapppayAdminService.listLogs({ page: logPage + 1, limit: logRows, action: logAction, status: logStatus, search: logSearch });
      setLogs(r?.docs || []);
      setLogsTotal(r?.total || 0);
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || e?.message || 'خطا در دریافت لاگ‌ها', { variant: 'error' });
    } finally {
      setLogsLoading(false);
    }
  }, [logPage, logRows, logAction, logStatus, logSearch, enqueueSnackbar]);
  useEffect(() => { if (tab === 2) loadLogs(); }, [tab, loadLogs]);

  // ── tools ──
  const [connTest, setConnTest] = useState<SnappPayConnectionTest | null>(null);
  const [connTesting, setConnTesting] = useState(false);
  const [eligAmount, setEligAmount] = useState<string>('500000');
  const [eligTypes, setEligTypes] = useState<string[]>([]);
  const [eligResult, setEligResult] = useState<SnappPayEligibleTest | null>(null);
  const [eligTesting, setEligTesting] = useState(false);
  const [toolBusy, setToolBusy] = useState<string | null>(null);

  const runConnTest = async () => {
    setConnTesting(true); setConnTest(null);
    try { setConnTest(await snapppayAdminService.testConnection()); }
    catch (e: any) { enqueueSnackbar(e?.response?.data?.message || e?.message || 'اجرای تست ناموفق بود', { variant: 'error' }); }
    finally { setConnTesting(false); }
  };
  const runEligTest = async () => {
    const amt = Number(String(eligAmount).replace(/[^\d]/g, ''));
    if (!(amt > 0)) { enqueueSnackbar('مبلغ معتبر وارد کنید', { variant: 'warning' }); return; }
    setEligTesting(true); setEligResult(null);
    try { setEligResult(await snapppayAdminService.testEligible(amt, eligTypes.length ? eligTypes : undefined)); }
    catch (e: any) { enqueueSnackbar(e?.response?.data?.message || e?.message || 'استعلام ناموفق بود', { variant: 'error' }); }
    finally { setEligTesting(false); }
  };
  const runTool = async (kind: 'sweep' | 'refresh') => {
    setToolBusy(kind);
    try {
      const r: any = kind === 'sweep' ? await snapppayAdminService.sweepSettle(100) : await snapppayAdminService.refreshStatuses(100);
      enqueueSnackbar(r?.message || 'انجام شد', { variant: 'success' });
      loadOverview();
      if (tab === 1) loadOrders();
    } catch (e: any) {
      enqueueSnackbar(e?.response?.data?.message || e?.message || 'اجرا ناموفق بود', { variant: 'error' });
    } finally {
      setToolBusy(null);
    }
  };

  // ---------------------------------------------------------------- columns

  const orderColumns: Column<SnappPayOrderRow>[] = useMemo(() => [
    { id: 'orderNumber', label: 'سفارش', priority: 1, format: (v, row) => (
      <Box>
        <Typography variant="body2" fontWeight={700}><Mono>{v}</Mono></Typography>
        <Typography variant="caption" color="text.secondary">{row.user?.name || '—'}{row.user?.phone ? ` · ${fa(row.user.phone)}` : ''}</Typography>
      </Box>
    ) },
    { id: 'transactionId', label: 'شناسهٔ تراکنش اسنپ‌پی', priority: 2, format: (v) => v ? <Mono copy={v}>{v}</Mono> : '—' },
    { id: 'snapppayStatus', label: 'وضعیت در اسنپ‌پی', priority: 3, format: (v, row) => (
      <Stack spacing={0.5} alignItems="flex-start">
        <StatusChip status={v} />
        {row.snapppaySyncError && <Tooltip title={row.snapppaySyncError}><Chip size="small" icon={<SyncProblemIcon sx={{ fontSize: 14 }} />} label="خطای همگام‌سازی" color="warning" variant="outlined" sx={{ fontSize: 10.5 }} /></Tooltip>}
      </Stack>
    ) },
    { id: 'paidAmount', label: 'مبلغ پرداختی', priority: 4, align: 'left', format: (v, row) => (
      <Box sx={{ textAlign: 'left' }}>
        <Typography variant="body2" fontWeight={700}>{fmtToman(v ?? row.payableAmount ?? row.totalAmount)}</Typography>
        {row.surcharge && row.surcharge.amount > 0 && (
          <Typography variant="caption" color="warning.dark">شامل کارمزد {fmtToman(row.surcharge.amount)} ({fa(row.surcharge.ratePercent)}٪)</Typography>
        )}
      </Box>
    ) },
    { id: 'paidAt', label: 'زمان پرداخت', hideOnMobile: true, format: (v, row) => v ? formatDate(v) : formatDate(row.createdAt) },
    { id: 'paymentStatus', label: 'وضعیت سفارش', hideOnMobile: true, detail: true, format: (v, row) => `${row.status} / ${v}` },
  ], []);

  const logColumns: Column<SnappPayLog>[] = useMemo(() => [
    { id: 'createdAt', label: 'زمان', priority: 1, format: (v) => formatDate(v) },
    { id: 'action', label: 'عملیات', priority: 2, format: (v, row) => (
      <Box>
        <Typography variant="body2" fontWeight={700}>{LOG_ACTION_LABELS[v] || v}</Typography>
        <Typography variant="caption" color="text.secondary">{row.message || ''}</Typography>
      </Box>
    ) },
    { id: 'status', label: 'نتیجه', priority: 3, format: (v, row) => (
      <Stack spacing={0.3}>
        <Chip size="small" label={v === 'success' ? 'موفق' : v === 'failed' ? 'ناموفق' : 'در انتظار'} sx={{ bgcolor: alpha(LOG_STATUS_COLORS[v] || '#64748B', 0.12), color: LOG_STATUS_COLORS[v] || '#64748B', fontWeight: 700, fontSize: 11 }} />
        {row.code !== undefined && row.code !== null && <Typography variant="caption" color="text.secondary">کد {fa(row.code)}</Typography>}
      </Stack>
    ) },
    { id: 'refId', label: 'شناسهٔ تراکنش', priority: 4, format: (v, row) => (
      <Box>
        {v ? <Mono copy={v}>{v}</Mono> : '—'}
        {row.orderId && typeof row.orderId === 'object' && <Typography variant="caption" display="block" color="text.secondary">سفارش <Mono>{row.orderId.orderNumber}</Mono></Typography>}
      </Box>
    ) },
    { id: 'amount', label: 'مبلغ', align: 'left', hideOnMobile: true, format: (v) => fmtToman(v) },
    { id: 'error', label: 'خطا', detail: true, format: (v) => v ? <Typography variant="caption" color="error.main" sx={{ wordBreak: 'break-word' }}>{v}</Typography> : '—' },
  ], []);

  // ---------------------------------------------------------------- render helpers

  const counts = overview?.counts || {};
  const statusCount = (s: string) => counts[s]?.count || 0;

  const renderOverview = () => (
    <Box>
      {overview && !overview.configured && (
        <Alert severity="error" sx={{ mb: 2 }}>اعتبارنامه‌های اسنپ‌پی در env سرور کامل نیست (SNAPPPAY_CLIENT_ID / CLIENT_SECRET / USERNAME / PASSWORD). تا تکمیل آن، درگاه به کاربران نمایش داده نمی‌شود.</Alert>
      )}
      {overview && overview.configured && !overview.settings.enabled && (
        <Alert severity="warning" sx={{ mb: 2 }}>درگاه اسنپ‌پی از تنظیمات <b>خاموش</b> است؛ گزینهٔ «خرید اقساطی اسنپ‌پی» در چک‌اوت نمایش داده نمی‌شود. از «تنظیمات و کارمزد» (بالای صفحه) روشن کنید.</Alert>
      )}
      {overview && overview.surcharge.percent <= 0 && (
        <Alert severity="info" sx={{ mb: 2 }}>کارمزد خرید اقساطی اسنپ‌پی روی <b>صفر</b> است. اگر قرار است کارمزد از خریدار گرفته شود، «کارمزد خرید اقساطی اسنپ‌پی (درصد)» را در تب تنظیمات مقداردهی کنید.</Alert>
      )}
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={6} md={2}><StatsCard title="کل سفارش‌های اسنپ‌پی" value={fa(overview?.total || 0)} icon={<ReceiptLongIcon />} iconBgColor={SNAPP_BLUE} loading={overviewLoading} /></Grid>
        <Grid item xs={6} md={2}><StatsCard title="نهایی‌شده (SETTLE)" value={fa(statusCount('SETTLE'))} subtitle={fmtToman(counts.SETTLE?.amount || 0)} icon={<CheckCircleIcon />} iconBgColor="#16A34A" loading={overviewLoading} /></Grid>
        <Grid item xs={6} md={2}><StatsCard title="منتظر نهایی‌سازی" value={fa(overview?.unsettled || 0)} subtitle="VERIFY / PENDING" icon={<HourglassTopIcon />} iconBgColor="#D97706" loading={overviewLoading} /></Grid>
        <Grid item xs={6} md={2}><StatsCard title="لغو / برگشت" value={fa(statusCount('CANCEL') + statusCount('REVERT'))} icon={<CancelIcon />} iconBgColor="#DC2626" loading={overviewLoading} /></Grid>
        <Grid item xs={6} md={2}><StatsCard title="خطای همگام‌سازی" value={fa(overview?.syncErrors || 0)} icon={<SyncProblemIcon />} iconBgColor="#9333EA" loading={overviewLoading} /></Grid>
        <Grid item xs={6} md={2}><StatsCard title="پرداخت ۲۴ ساعت اخیر" value={fa(overview?.paidLast24h || 0)} subtitle={`کارمزد جمع: ${fmtToman(Object.values(counts).reduce((s, c) => s + (c.surcharge || 0), 0))}`} icon={<BoltIcon />} iconBgColor="#0EA5E9" loading={overviewLoading} /></Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>پیکربندی فعلی</Typography>
            <Row label="محیط" value={overview ? <Chip size="small" label={overview.environment === 'staging' ? 'استیج (تست)' : 'پروداکشن'} color={overview.environment === 'staging' ? 'warning' : 'success'} /> : '—'} />
            <Row label="آدرس API" value={<Mono copy={overview?.apiBase}>{overview?.apiBase || '—'}</Mono>} />
            <Row label="آدرس بازگشت (returnURL)" value={<Mono copy={overview?.returnUrl}>{overview?.returnUrl || '—'}</Mono>} />
            <Row label="اعتبارنامه" value={overview?.configured ? <Chip size="small" color="success" label="کامل" /> : <Chip size="small" color="error" label="ناقص" />} />
            <Row label="فعال برای کاربران" value={overview?.settings.enabled ? <Chip size="small" color="success" label="روشن" /> : <Chip size="small" label="خاموش" />} />
            <Row label="استعلام eligible" value={overview ? ({ off: 'خاموش (همیشه نمایش)', soft: 'نرم (خطا ⇒ نمایش)', strict: 'سخت‌گیرانه (فقط با true)' } as any)[overview.settings.eligibilityMode] : '—'} />
            <Row label="کارمزد خرید اقساطی" value={overview ? `${fa(overview.surcharge.percent)}٪ (${overview.surcharge.mode === 'gross_up' ? 'جبرانی' : 'روی مبلغ سفارش'})` : '—'} />
            <Row label="بازهٔ مبلغ" value={overview ? `${overview.settings.minAmount > 0 ? fmtToman(overview.settings.minAmount) : 'بدون حداقل'} تا ${overview.settings.maxAmount > 0 ? fmtToman(overview.settings.maxAmount) : 'بدون حداکثر'}` : '—'} />
            <Row label="commissionType / دسته" value={overview ? `${fa(overview.settings.commissionType)} / ${overview.settings.itemCategory}` : '—'} />
            <Row label="روش‌های اجباری" value={overview?.settings.forcedMethodTypes?.length ? overview.settings.forcedMethodTypes.join('، ') : 'همهٔ روش‌های فعال حساب کاربر'} />
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
              <Button size="small" variant="outlined" onClick={() => navigate('/settings?tab=snapppay')}>ویرایش تنظیمات</Button>
              <Button size="small" variant="outlined" onClick={() => setTab(3)}>تست اتصال</Button>
            </Stack>
          </Paper>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>به تفکیک وضعیت اسنپ‌پی</Typography>
            {(overview?.statuses || []).concat(counts.UNKNOWN ? ['UNKNOWN'] : []).map((s) => (
              <Box key={s} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.6, borderBottom: '1px dashed', borderColor: 'divider' }}>
                <StatusChip status={s} />
                <Box sx={{ textAlign: 'left' }}>
                  <Typography variant="body2" fontWeight={700}>{fa(statusCount(s))} سفارش</Typography>
                  <Typography variant="caption" color="text.secondary">{fmtToman(counts[s]?.amount || 0)}</Typography>
                </Box>
              </Box>
            ))}
            <Button size="small" sx={{ mt: 1 }} onClick={() => { setStatusFilter('UNSETTLED'); setTab(1); }}>نمایش سفارش‌های منتظر نهایی‌سازی</Button>
          </Paper>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>فعالیت ۲۴ ساعت اخیر (لاگ‌ها)</Typography>
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {Object.entries(overview?.attempts24h || {}).length === 0 && <Typography variant="body2" color="text.secondary">فعالیتی ثبت نشده است</Typography>}
              {Object.entries(overview?.attempts24h || {}).map(([k, n]) => {
                const [action, status] = k.split(':');
                return <Chip key={k} size="small" label={`${LOG_ACTION_LABELS[action] || action} · ${status === 'success' ? 'موفق' : status === 'failed' ? 'ناموفق' : 'در انتظار'}: ${fa(n)}`} sx={{ bgcolor: alpha(LOG_STATUS_COLORS[status] || '#64748B', 0.1), color: LOG_STATUS_COLORS[status] || '#64748B', fontWeight: 700 }} />;
              })}
            </Stack>
            <Stack direction="row" spacing={1} sx={{ mt: 1.5 }} flexWrap="wrap" useFlexGap>
              <Button size="small" variant="contained" startIcon={<PlayArrowIcon />} disabled={!!toolBusy} onClick={() => { if (window.confirm('نهایی‌سازی (settle) برای همهٔ سفارش‌های پرداخت‌شدهٔ نهایی‌نشده در اسنپ‌پی اجرا شود؟ این کار روی چند سفارش واقعی اثر می‌گذارد و بعد از settle امکان revert وجود ندارد.')) runTool('sweep'); }}>{toolBusy === 'sweep' ? 'در حال اجرا…' : 'نهایی‌سازی سفارش‌های مانده (settle)'}</Button>
              <Button size="small" variant="outlined" startIcon={<RefreshIcon />} disabled={!!toolBusy} onClick={() => runTool('refresh')}>{toolBusy === 'refresh' ? 'در حال استعلام…' : 'استعلام وضعیت دسته‌ای'}</Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );

  const renderOrders = () => (
    <Box>
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <TextField
            size="small"
            fullWidth
            placeholder="شناسهٔ تراکنش اسنپ‌پی، توکن پرداخت یا شمارهٔ سفارش…"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setPage(0); setSearch(searchInput.trim()); setSearchParams(searchInput.trim() ? { tab: '1', search: searchInput.trim() } : { tab: '1' }); } }}
            InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 18, color: 'text.disabled', ml: 0.5 }} /> }}
          />
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel>وضعیت در اسنپ‌پی</InputLabel>
            <Select label="وضعیت در اسنپ‌پی" value={statusFilter} onChange={(e) => { setPage(0); setStatusFilter(String(e.target.value)); }}>
              <MenuItem value="">همه</MenuItem>
              <MenuItem value="UNSETTLED">منتظر نهایی‌سازی (VERIFY/PENDING)</MenuItem>
              {['PENDING', 'VERIFY', 'SETTLE', 'CANCEL', 'REVERT'].map((s) => <MenuItem key={s} value={s}>{SNAPP_STATUS_LABELS[s]}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControlLabel control={<Switch size="small" checked={onlySyncError} onChange={(e) => { setPage(0); setOnlySyncError(e.target.checked); }} />} label={<Typography variant="body2">فقط خطای همگام‌سازی</Typography>} />
          <Button variant="contained" size="small" onClick={() => { setPage(0); setSearch(searchInput.trim()); }}>جست‌وجو</Button>
          <IconButton size="small" onClick={loadOrders}><RefreshIcon /></IconButton>
        </Stack>
      </Paper>
      <ResponsiveTable<SnappPayOrderRow>
        columns={orderColumns}
        data={orders}
        loading={ordersLoading}
        keyField="_id"
        emptyMessage="سفارشی با اسنپ‌پی یافت نشد"
        pagination
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={ordersTotal}
        onPageChange={setPage}
        onRowsPerPageChange={(n) => { setRowsPerPage(n); setPage(0); }}
        onRowClick={(row) => setDetailId(row._id)}
        renderActions={(row) => (
          <Stack direction="row" spacing={0.5}>
            <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); setDetailId(row._id); }}>مدیریت</Button>
            <Tooltip title="باز کردن در بخش سفارش‌ها"><IconButton size="small" onClick={(e) => { e.stopPropagation(); navigate(`/orders?search=${encodeURIComponent(row.orderNumber)}`); }}><OpenInNewIcon fontSize="small" /></IconButton></Tooltip>
          </Stack>
        )}
      />
    </Box>
  );

  const renderDetailDialog = () => {
    const o = detail?.order;
    const cap = detail?.capabilities;
    return (
      <Dialog open={!!detailId} onClose={() => (busy ? undefined : setDetailId(null))} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <SnappLogo size={30} />
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle1" fontWeight={800}>مدیریت سفارش اسنپ‌پی {o ? <Mono>{o.orderNumber}</Mono> : ''}</Typography>
            {o && <Typography variant="caption" color="text.secondary">{o.user?.name || '—'}{o.user?.phone ? ` · ${fa(o.user.phone)}` : ''} · ثبت {formatDate(o.createdAt)}</Typography>}
          </Box>
          {o && (detail?.attemptOnly ? <Chip size="medium" label="تلاش نهایی‌نشده" color="warning" variant="outlined" /> : <StatusChip status={o.snapppayStatus} size="medium" />)}
        </DialogTitle>
        <DialogContent dividers>
          {detailLoading || !detail || !o ? (
            <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress size={28} /></Box>
          ) : (
            <Grid container spacing={2}>
              {detail.attemptOnly && (
                <Grid item xs={12}>
                  <Alert severity="warning">{detail.attemptNote || 'پرداخت این سفارش با اسنپ‌پی نهایی نشده است.'} فقط استعلام وضعیت و مشاهدهٔ لاگ‌ها در دسترس است؛ عملیات مالی (settle/update/cancel) برای این سفارش معنا ندارد.</Alert>
                </Grid>
              )}
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>تراکنش</Typography>
                  <Row label="شناسهٔ تراکنش (ما ↔ اسنپ‌پی)" value={o.transactionId ? <Mono copy={o.transactionId}>{o.transactionId}</Mono> : '—'} />
                  <Row label="توکن پرداخت" value={o.paymentToken ? <Mono copy={o.paymentToken}>{o.paymentToken}</Mono> : '—'} />
                  <Row label="مبلغ سفارش (کالا)" value={fmtToman(o.totalAmount)} />
                  <Row label="کارمزد خرید اقساطی" value={o.surcharge && o.surcharge.amount > 0 ? `${fmtToman(o.surcharge.amount)} (${fa(o.surcharge.ratePercent)}٪ ${o.surcharge.mode === 'gross_up' ? 'جبرانی' : 'روی مبلغ'})` : 'بدون کارمزد'} />
                  <Row label="مبلغ فعلی نزد اسنپ‌پی" value={<b>{fmtToman(o.paidAmount ?? o.payableAmount ?? o.totalAmount)}</b>} />
                  <Row label="زمان تأیید (verify)" value={o.verifiedAt ? formatDate(o.verifiedAt) : '—'} />
                  <Row label="نهایی‌سازی (settle)" value={o.snapppaySettledAt ? formatDate(o.snapppaySettledAt) : 'هنوز انجام نشده'} />
                  {o.snapppayCancelledAt && <Row label="لغو / برگشت" value={formatDate(o.snapppayCancelledAt)} />}
                  <Row label="وضعیت سفارش در کازماشاپ" value={`${o.status} / ${o.paymentStatus}`} />
                  {o.snapppaySyncError && <Alert severity="warning" sx={{ mt: 1, fontSize: 12 }}>{o.snapppaySyncError}</Alert>}
                </Paper>
              </Grid>
              <Grid item xs={12} md={6}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>عملیات اسنپ‌پی</Typography>
                  <Stack spacing={1}>
                    <Button variant="outlined" size="small" disabled={!!busy || cap?.canStatus === false} onClick={() => runAction('status')}>{busy === 'status' ? 'در حال استعلام…' : 'استعلام وضعیت (Get Payment Status)'}</Button>
                    {cap?.canSettle && <Button variant="contained" size="small" color="success" disabled={!!busy} onClick={() => setConfirm({ kind: 'settle' })}>{busy === 'settle' ? 'در حال نهایی‌سازی…' : 'نهایی‌سازی (settle)'}</Button>}
                    {cap?.canUpdate && <Button variant="outlined" size="small" color="warning" disabled={!!busy} onClick={() => setConfirm({ kind: 'update', amount: '' })}>کاهش مبلغ / حذف آیتم (update)</Button>}
                    {cap?.canCancel && <Button variant="outlined" size="small" color="error" disabled={!!busy} onClick={() => setConfirm({ kind: 'cancel' })}>لغو کامل و استرداد (cancel)</Button>}
                    {cap?.canRevert && <Button variant="text" size="small" color="secondary" disabled={!!busy} onClick={() => setConfirm({ kind: 'revert' })}>بازگشت وجه قبل از نهایی‌سازی (revert)</Button>}
                  </Stack>
                  <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ mt: 1.5, fontSize: 11.5, lineHeight: 1.9 }}>
                    cancel و update برگشت‌ناپذیرند و مبلغ را به اعتبار خریدار نزد اسنپ‌پی برمی‌گردانند؛ برای سفارش اسنپ‌پی استرداد داخلی به کیف پول انجام ندهید.
                    حذف آیتم از بخش سفارش‌ها (استرداد آیتم) هم به‌صورت خودکار update اسنپ‌پی را می‌زند.
                  </Alert>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>آیتم‌های سفارش ({fa(detail.items.length)}) — جمع آیتم‌های فعال: {fmtToman(detail.activeItemsSum)}</Typography>
                  <Box sx={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
                      <thead><tr style={{ textAlign: 'right', color: '#64748B' }}><th style={{ padding: 6 }}>کالا</th><th style={{ padding: 6 }}>تعداد</th><th style={{ padding: 6 }}>قیمت واحد</th><th style={{ padding: 6 }}>جمع</th><th style={{ padding: 6 }}>وضعیت</th><th style={{ padding: 6 }}>مسترد شده</th></tr></thead>
                      <tbody>
                        {detail.items.map((it) => {
                          const inactive = ['cancelled', 'refunded'].includes(it.status);
                          return (
                            <tr key={it._id} style={{ borderTop: '1px solid #eee', opacity: inactive ? 0.55 : 1, textDecoration: inactive ? 'line-through' : 'none' }}>
                              <td style={{ padding: 6 }}>{it.productTitle}{it.storeName ? <span style={{ color: '#64748B' }}> · {it.storeName}</span> : ''}</td>
                              <td style={{ padding: 6 }}>{fa(it.quantity)}</td>
                              <td style={{ padding: 6 }}>{fmtToman(it.unitPrice)}</td>
                              <td style={{ padding: 6 }}>{fmtToman(it.totalPrice)}</td>
                              <td style={{ padding: 6 }}>{it.status}</td>
                              <td style={{ padding: 6 }}>{it.refundedAmount ? fmtToman(it.refundedAmount) : '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </Box>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    برای حذف یک آیتم از سفارش اسنپ‌پی، از بخش «سفارش‌ها» همان آیتم را بازپرداخت کنید؛ سیستم خودکار update اسنپ‌پی را با مبلغ جدید می‌زند. اگر آخرین آیتم باشد، cancel زده می‌شود.
                  </Typography>
                </Paper>
              </Grid>
              <Grid item xs={12}>
                <Paper variant="outlined" sx={{ p: 1.5 }}>
                  <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 0.5 }}>تایم‌لاین اسنپ‌پی ({fa(detail.logs.length)})</Typography>
                  {detail.logs.length === 0 ? <Typography variant="body2" color="text.secondary">لاگی ثبت نشده</Typography> : (
                    <Stack spacing={0.75}>
                      {detail.logs.map((l) => (
                        <Box key={l._id} sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', p: 1, borderRadius: 1, bgcolor: alpha(LOG_STATUS_COLORS[l.status] || '#64748B', 0.06) }}>
                          <Box sx={{ width: 8, height: 8, borderRadius: '50%', mt: 0.8, bgcolor: LOG_STATUS_COLORS[l.status] || '#64748B', flexShrink: 0 }} />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography variant="body2" fontWeight={700}>{LOG_ACTION_LABELS[l.action] || l.action} — {l.message || (l.status === 'success' ? 'موفق' : l.status)}{l.code !== undefined && l.code !== null ? ` (کد ${fa(l.code)})` : ''}</Typography>
                            <Typography variant="caption" color="text.secondary">{formatDate(l.createdAt)}{l.amount ? ` · ${fmtToman(l.amount)}` : ''}{l.meta?.snapppayStatus ? ` · وضعیت: ${l.meta.snapppayStatus}` : ''}{l.meta?.source ? ` · منبع: ${l.meta.source}` : ''}</Typography>
                            {l.error && <Typography variant="caption" color="error.main" sx={{ display: 'block', wordBreak: 'break-word' }}>{l.error}</Typography>}
                          </Box>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </Paper>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {o && <Button startIcon={<OpenInNewIcon />} onClick={() => navigate(`/orders?search=${encodeURIComponent(o.orderNumber)}`)}>باز کردن در سفارش‌ها</Button>}
          <Box sx={{ flex: 1 }} />
          <Button onClick={() => setDetailId(null)} disabled={!!busy}>بستن</Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderConfirmDialog = () => {
    if (!confirm || !detail) return null;
    const o = detail.order;
    const paid = Math.round(Number(o.paidAmount ?? o.payableAmount ?? o.totalAmount) || 0);
    const titles: Record<string, string> = { cancel: 'لغو کامل سفارش در اسنپ‌پی', revert: 'بازگشت وجه قبل از نهایی‌سازی (revert)', update: 'کاهش مبلغ سفارش در اسنپ‌پی (update)', settle: 'نهایی‌سازی پرداخت (settle)' };
    const amountNum = confirm.amount && confirm.amount.trim() !== '' ? Number(confirm.amount.replace(/[^\d]/g, '')) : undefined;
    // کارمزد اقساطیِ هنوز نزد ما (charged) ⇒ سوئیچ «کارمزد هم برگردد» با پیش‌فرض تنظیم اسنپ‌پی (پیش‌فرض روشن = cancel کامل)
    const feeOutstanding = o.surcharge && o.surcharge.amount > 0 && o.surcharge.status === 'charged' ? Math.round(Number(o.surcharge.amount)) : 0;
    const includeFee = confirm.includeSurcharge ?? (overview?.refundIncludesSurcharge ?? true);
    const feeSwitch = feeOutstanding > 0 && (confirm.kind === 'cancel' || confirm.kind === 'update') ? (
      <FormControlLabel
        sx={{ m: 0, mb: 1, alignItems: 'flex-start' }}
        control={<Switch size="small" checked={includeFee} onChange={(e) => setConfirm({ ...confirm, includeSurcharge: e.target.checked })} />}
        label={<Typography variant="body2">کارمزد اقساطی <b>{fmtToman(feeOutstanding)}</b> هم به خریدار برگردد{includeFee ? ' (cancel کامل)' : ' (خاموش: به‌جای cancel، مبلغ تراکنش به «فقط کارمزد» update می‌شود و اقساط خریدار روی همان مبلغ می‌ماند؛ اگر اسنپ‌پی نپذیرد لغو کامل می‌شود)'}</Typography>}
      />
    ) : null;
    return (
      <Dialog open onClose={() => (busy ? undefined : setConfirm(null))} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>{titles[confirm.kind]}</DialogTitle>
        <DialogContent dividers>
          {confirm.kind === 'cancel' && <Alert severity="error" sx={{ mb: 1 }}>{includeFee || feeOutstanding === 0 ? <>کل مبلغ <b>{fmtToman(paid)}</b> (شامل کارمزد) به اعتبار خریدار نزد اسنپ‌پی برمی‌گردد</> : <>ارزش کالا <b>{fmtToman(Math.max(0, paid - feeOutstanding))}</b> به اعتبار خریدار نزد اسنپ‌پی برمی‌گردد و کارمزد {fmtToman(feeOutstanding)} می‌ماند</>} و سفارش در کازماشاپ «مسترد» می‌شود. <b>این عملیات برگشت‌ناپذیر است.</b> قبل از لغو از عدم تحویل کالا مطمئن شوید.</Alert>}
          {(confirm.kind === 'cancel' || confirm.kind === 'update') && feeSwitch}
          {(confirm.kind === 'cancel' || confirm.kind === 'update') && <RefundPreviewSteps preview={confirmPreview} loading={confirmPreviewLoading} error={confirmPreviewError} />}
          {confirm.kind === 'revert' && <Alert severity="warning" sx={{ mb: 1 }}>revert فقط برای تراکنشی ممکن است که هنوز settle نشده (PENDING/VERIFY). مبلغ کسرشده به حساب خریدار برمی‌گردد و تراکنش ناموفق تلقی می‌شود. اگر سفارش پرداخت‌شده ثبت شده، «مسترد» می‌شود.</Alert>}
          {confirm.kind === 'settle' && <Alert severity="info" sx={{ mb: 1 }}>settle خرید را از سمت اسنپ‌پی نهایی می‌کند (الزامی بعد از verify). بعد از settle فقط cancel/update ممکن است، نه revert.</Alert>}
          {confirm.kind === 'update' && (
            <Box>
              <Alert severity="warning" sx={{ mb: 1.5 }}>مبلغ جدید باید کمتر از مبلغ فعلی ({fmtToman(paid)}) باشد؛ تفاوت به اعتبار خریدار نزد اسنپ‌پی برمی‌گردد. <b>برگشت‌ناپذیر است.</b> خالی بگذارید تا از مجموع آیتم‌های فعال + کارمزد محاسبه شود ({fmtToman(detail.activeItemsSum + (o.surcharge?.amount || 0))}).</Alert>
              <TextField
                fullWidth
                size="small"
                label="مبلغ جدید (تومان) — اختیاری"
                value={confirm.amount || ''}
                onChange={(e) => setConfirm({ kind: 'update', amount: e.target.value })}
                placeholder={String(detail.activeItemsSum + (o.surcharge?.amount || 0))}
                inputProps={{ inputMode: 'numeric', dir: 'ltr' }}
              />
              {amountNum !== undefined && amountNum >= paid && <Typography variant="caption" color="error.main">مبلغ جدید باید کمتر از {fmtToman(paid)} باشد</Typography>}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)} disabled={!!busy}>انصراف</Button>
          <Button
            variant="contained"
            color={confirm.kind === 'cancel' ? 'error' : confirm.kind === 'update' ? 'warning' : confirm.kind === 'settle' ? 'success' : 'secondary'}
            disabled={!!busy || (confirm.kind === 'update' && amountNum !== undefined && (!(amountNum > 0) || amountNum >= paid))}
            onClick={() => runAction(confirm.kind, confirm.kind === 'update' ? amountNum : undefined, feeOutstanding > 0 && (confirm.kind === 'cancel' || confirm.kind === 'update') ? includeFee : undefined)}
          >
            {busy ? 'در حال انجام…' : 'تأیید و ارسال به اسنپ‌پی'}
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderLogs = () => (
    <Box>
      <Paper variant="outlined" sx={{ p: 1.5, mb: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ md: 'center' }}>
          <TextField size="small" fullWidth placeholder="شناسهٔ تراکنش، توکن، پیام یا متن خطا…" value={logSearchInput} onChange={(e) => setLogSearchInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { setLogPage(0); setLogSearch(logSearchInput.trim()); } }} InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 18, color: 'text.disabled', ml: 0.5 }} /> }} />
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>عملیات</InputLabel>
            <Select label="عملیات" value={logAction} onChange={(e) => { setLogPage(0); setLogAction(String(e.target.value)); }}>
              <MenuItem value="">همه</MenuItem>
              {Object.entries(LOG_ACTION_LABELS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>نتیجه</InputLabel>
            <Select label="نتیجه" value={logStatus} onChange={(e) => { setLogPage(0); setLogStatus(String(e.target.value)); }}>
              <MenuItem value="">همه</MenuItem>
              <MenuItem value="success">موفق</MenuItem>
              <MenuItem value="failed">ناموفق</MenuItem>
              <MenuItem value="pending">در انتظار</MenuItem>
            </Select>
          </FormControl>
          <Button variant="contained" size="small" onClick={() => { setLogPage(0); setLogSearch(logSearchInput.trim()); }}>جست‌وجو</Button>
          <IconButton size="small" onClick={loadLogs}><RefreshIcon /></IconButton>
        </Stack>
      </Paper>
      <ResponsiveTable<SnappPayLog>
        columns={logColumns}
        data={logs}
        loading={logsLoading}
        keyField="_id"
        emptyMessage="لاگی ثبت نشده است"
        pagination
        page={logPage}
        rowsPerPage={logRows}
        totalCount={logsTotal}
        onPageChange={setLogPage}
        onRowsPerPageChange={(n) => { setLogRows(n); setLogPage(0); }}
        onRowClick={(row) => setExpandedLog(expandedLog === row._id ? null : row._id)}
        renderActions={(row) => (
          <Stack direction="row" spacing={0.5}>
            <Button size="small" onClick={(e) => { e.stopPropagation(); setExpandedLog(expandedLog === row._id ? null : row._id); }}>{expandedLog === row._id ? 'بستن' : 'جزئیات'}</Button>
            {row.orderId && typeof row.orderId === 'object' && <Button size="small" onClick={(e) => { e.stopPropagation(); setDetailId(String((row.orderId as any)._id)); }}>سفارش</Button>}
          </Stack>
        )}
      />
      {(() => {
        const l = expandedLog ? logs.find((x) => x._id === expandedLog) : null;
        return (
          <Dialog open={!!l} onClose={() => setExpandedLog(null)} maxWidth="md" fullWidth>
            {l && (
              <>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <SnappLogo size={28} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" fontWeight={800}>جزئیات لاگ — {LOG_ACTION_LABELS[l.action] || l.action}</Typography>
                    <Typography variant="caption" color="text.secondary">{formatDate(l.createdAt)}{l.message ? ` · ${l.message}` : ''}</Typography>
                  </Box>
                  <Chip size="small" label={l.status === 'success' ? 'موفق' : l.status === 'failed' ? 'ناموفق' : 'در انتظار'} sx={{ bgcolor: alpha(LOG_STATUS_COLORS[l.status] || '#64748B', 0.12), color: LOG_STATUS_COLORS[l.status] || '#64748B', fontWeight: 700 }} />
                </DialogTitle>
                <DialogContent dividers>
                  <LogDetails log={l} />
                </DialogContent>
                <DialogActions>
                  {l.orderId && typeof l.orderId === 'object' && <Button startIcon={<OpenInNewIcon />} onClick={() => { setExpandedLog(null); setDetailId(String((l.orderId as any)._id)); }}>مدیریت سفارش</Button>}
                  <Box sx={{ flex: 1 }} />
                  <Button onClick={() => setExpandedLog(null)}>بستن</Button>
                </DialogActions>
              </>
            )}
          </Dialog>
        );
      })()}
    </Box>
  );



  const renderTools = () => (
    <Grid container spacing={2}>
      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>تست اتصال (DNS + احراز هویت OAuth + eligible)</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>هیچ تراکنشی ثبت نمی‌کند. برای اطمینان از وایت‌لیست بودن IP سرور و درستی اعتبارنامه‌ها.</Typography>
          <Button variant="contained" size="small" disabled={connTesting} onClick={runConnTest}>{connTesting ? 'در حال تست…' : 'اجرای تست اتصال'}</Button>
          {connTest && (
            <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 1, bgcolor: alpha(connTest.login.ok ? '#16A34A' : '#DC2626', 0.08), fontSize: 13, lineHeight: 2 }}>
              <div><b>آدرس:</b> <Mono>{connTest.apiBase}</Mono></div>
              <div><b>پیکربندی:</b> {connTest.configured ? 'کامل' : 'ناقص'}</div>
              <div><b>DNS:</b> {connTest.dns.ok ? `OK (${connTest.dns.addresses.join(', ')})` : `ناموفق ${connTest.dns.error || ''}`}</div>
              <div><b>لاگین:</b> {connTest.login.ok ? `موفق (${fa(connTest.login.ms)} ms)` : `ناموفق ${connTest.login.error || ''}`}</div>
              <div><b>eligible نمونه:</b> {connTest.eligible.ok ? `${connTest.eligible.eligible ? 'true' : 'false'} — ${connTest.eligible.title} / ${connTest.eligible.description}` : (connTest.eligible.error || '—')}</div>
              <div style={{ marginTop: 6, fontWeight: 700 }}>{connTest.hint}</div>
            </Box>
          )}
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>استعلام eligible برای یک مبلغ</Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>همان چیزی که کاربر در چک‌اوت می‌بیند: کارمزد اضافه می‌شود و متن عنوان/توضیح عیناً از اسنپ‌پی می‌آید.</Typography>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
            <TextField size="small" label="مبلغ سفارش (تومان)" value={eligAmount} onChange={(e) => setEligAmount(e.target.value)} inputProps={{ dir: 'ltr', inputMode: 'numeric' }} sx={{ flex: 1 }} />
            <Button variant="contained" size="small" disabled={eligTesting} onClick={runEligTest}>{eligTesting ? '…' : 'استعلام'}</Button>
          </Stack>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {['INSTALLMENT', 'POSTPAID', 'FINANCING'].map((m) => (
              <Chip key={m} size="small" label={m} clickable color={eligTypes.includes(m) ? 'primary' : 'default'} variant={eligTypes.includes(m) ? 'filled' : 'outlined'} onClick={() => setEligTypes(eligTypes.includes(m) ? eligTypes.filter((x) => x !== m) : [...eligTypes, m])} />
            ))}
            <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>(اختیاری: محدودکردن روش پرداخت)</Typography>
          </Stack>
          {eligResult && (
            <Box sx={{ p: 1.5, borderRadius: 1, bgcolor: alpha(eligResult.eligible === true ? '#16A34A' : eligResult.eligible === false ? '#D97706' : '#DC2626', 0.08), fontSize: 13, lineHeight: 2 }}>
              <div><b>مبلغ سفارش:</b> {fmtToman(eligResult.amount)} · <b>کارمزد:</b> {fmtToman(eligResult.surchargeAmount)} ({fa(eligResult.surchargePercent)}٪) · <b>قابل پرداخت:</b> {fmtToman(eligResult.payableAmount)}</div>
              <div><b>بازهٔ ادمین:</b> {eligResult.adminAvailable ? 'مجاز' : `غیرمجاز — ${eligResult.adminReason}`}</div>
              {eligResult.eligible !== null ? (
                <>
                  <div><b>پاسخ اسنپ‌پی:</b> {eligResult.eligible ? 'eligible = true ✅' : 'eligible = false ⛔ (گزینه پنهان می‌شود)'} ({fa(eligResult.ms)} ms)</div>
                  <div><b>عنوان:</b> {eligResult.titleMessage}</div>
                  <div><b>توضیح:</b> {eligResult.description}</div>
                </>
              ) : (
                <div style={{ color: '#DC2626' }}><b>خطا:</b> {eligResult.error}{eligResult.errorCode ? ` (کد ${fa(eligResult.errorCode)})` : ''}</div>
              )}
            </Box>
          )}
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>کارهای دسته‌ای</Typography>
          <Stack spacing={1}>
            <Button variant="contained" size="small" startIcon={<PlayArrowIcon />} disabled={!!toolBusy} onClick={() => { if (window.confirm('نهایی‌سازی (settle) برای همهٔ سفارش‌های پرداخت‌شدهٔ نهایی‌نشده در اسنپ‌پی اجرا شود؟ این کار روی چند سفارش واقعی اثر می‌گذارد و بعد از settle امکان revert وجود ندارد.')) runTool('sweep'); }}>{toolBusy === 'sweep' ? 'در حال اجرا…' : 'نهایی‌سازی سفارش‌های مانده (اجرای دستی کرون settle)'}</Button>
            <Button variant="outlined" size="small" startIcon={<RefreshIcon />} disabled={!!toolBusy} onClick={() => runTool('refresh')}>{toolBusy === 'refresh' ? 'در حال استعلام…' : 'استعلام وضعیت دسته‌ای سفارش‌های غیرنهایی / دارای خطا'}</Button>
          </Stack>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, lineHeight: 1.8 }}>
            کرون «snapppay-settle-retry» هر ۱۰ دقیقه خودکار اجرا می‌شود؛ این دکمه‌ها همان کار را همین حالا انجام می‌دهند. اسنپ‌پی هم تراکنش‌های VERIFY را شبانه خودش settle می‌کند.
          </Typography>
        </Paper>
      </Grid>
      <Grid item xs={12} md={6}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>اطلاعات لازم برای پشتیبانی اسنپ‌پی</Typography>
          <Row label="IP سرور (وایت‌لیست)" value={<Mono copy="176.65.241.166">176.65.241.166</Mono>} />
          <Row label="returnURL ثبت‌شده" value={<Mono copy={overview?.returnUrl}>{overview?.returnUrl || '—'}</Mono>} />
          <Row label="ایمیل پشتیبانی مرچنت" value={<Mono copy="merchant@snapppay.ir">merchant@snapppay.ir</Mono>} />
          <Row label="بررسی IP خروجی سرور" value={<Mono>curl https://whatisip.snapppay.ir/whatis/ip</Mono>} />
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, lineHeight: 1.8 }}>
            برای تست سناریوی رسمی: سبد کامل زیر ۱۰۰ هزار تومان ← پرداخت موفق ← دیدن شناسهٔ تراکنش در صفحهٔ نتیجه ← جست‌وجوی همان شناسه در «سفارش‌ها» ← حذف یک واحد (استرداد آیتم) ← ۳۰ ثانیه بعد حذف آیتم تخفیف‌دار ← لغو کامل.
          </Typography>
        </Paper>
      </Grid>
    </Grid>
  );

  const renderGuide = () => (
    <Box>
      <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
        <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>چرخهٔ عمر تراکنش اسنپ‌پی</Typography>
        <Box component="pre" sx={{ m: 0, p: 1.5, bgcolor: 'action.hover', borderRadius: 1, fontSize: 12.5, direction: 'ltr', textAlign: 'left', overflowX: 'auto' }}>
{`eligible → token (paymentPageUrl) → کاربر در درگاه → callback POST {transactionId, state, amount}
  state=OK     → verify (فقط یک بار) → VERIFY → settle → SETTLE → cancel → CANCEL
                                                        SETTLE → update (کاهش مبلغ؛ چند بار)
  state=FAILED → revert → REVERT
  PENDING/VERIFY → revert → REVERT   (قبل از settle)
تایم‌اوت/1000/1053 → Get Payment Status → VERIFY⇒settle · PENDING⇒verify · SETTLE⇒نهایی · REVERT/CANCEL⇒ناموفق`}
        </Box>
        <Typography variant="body2" sx={{ mt: 1.5, lineHeight: 2 }}>
          • همهٔ مبالغ API ریال است (سیستم خودکار تبدیل می‌کند). • شناسهٔ تراکنش ۵ تا ۱۰ کاراکتر و یکتا است. • بدون settle، پول به ما تسویه نمی‌شود. • cancel/update فقط از SETTLE و برگشت‌ناپذیرند. • در پروداکشن، مبلغ زیر ۴۰۰ هزار و بالای ۱۰ میلیون تومان eligible نمی‌شود.
        </Typography>
      </Paper>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="subtitle2" fontWeight={800} sx={{ mb: 1 }}>جدول کدهای خطای اسنپ‌پی و اقدام پیشنهادی</Typography>
        <Box sx={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12.5 }}>
            <thead><tr style={{ textAlign: 'right', color: '#64748B' }}><th style={{ padding: 8 }}>کد</th><th style={{ padding: 8 }}>HTTP</th><th style={{ padding: 8 }}>System</th><th style={{ padding: 8 }}>پیام</th><th style={{ padding: 8 }}>اقدام</th></tr></thead>
            <tbody>
              {SNAPP_ERROR_CODES.map((e) => (
                <tr key={`${e.code}-${e.http}`} style={{ borderTop: '1px solid #eee', verticalAlign: 'top' }}>
                  <td style={{ padding: 8, fontWeight: 700 }}>{fa(e.code)}</td>
                  <td style={{ padding: 8 }}>{fa(e.http)}</td>
                  <td style={{ padding: 8, direction: 'ltr', textAlign: 'right', fontFamily: 'monospace', fontSize: 11 }}>{e.system}</td>
                  <td style={{ padding: 8 }}>{e.message}</td>
                  <td style={{ padding: 8, lineHeight: 1.8 }}>{e.action}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      </Paper>
    </Box>
  );

  // ---------------------------------------------------------------- layout

  return (
    <Box>
      <PageHeader
        title="مرکز اسنپ‌پی"
        subtitle="مدیریت درگاه خرید اقساطی اسنپ‌پی: وضعیت اتصال، تنظیمات و کارمزد، سفارش‌ها و عملیات settle / update / cancel / revert، لاگ‌ها و ابزارهای تست"
        breadcrumbs={[{ label: 'مالی' }, { label: 'مرکز اسنپ‌پی' }]}
        actions={[{ label: 'تنظیمات و کارمزد', onClick: () => navigate('/settings?tab=snapppay'), icon: <SettingsIcon />, variant: 'outlined' }, { label: 'به‌روزرسانی', onClick: () => { loadOverview(); if (tab === 1) loadOrders(); if (tab === 2) loadLogs(); }, icon: <RefreshIcon />, variant: 'outlined' }]}
      >
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <SnappLogo size={34} />
          {overview && (
            <>
              <Chip size="small" label={overview.environment === 'staging' ? 'محیط استیج' : 'محیط پروداکشن'} color={overview.environment === 'staging' ? 'warning' : 'success'} />
              <Chip size="small" icon={overview.configured ? <CheckCircleIcon /> : <WarningAmberIcon />} label={overview.configured ? 'اعتبارنامه کامل' : 'اعتبارنامه ناقص'} color={overview.configured ? 'success' : 'error'} variant="outlined" />
              <Chip size="small" label={overview.settings.enabled ? 'فعال برای کاربران' : 'خاموش برای کاربران'} color={overview.settings.enabled ? 'primary' : 'default'} variant="outlined" />
              <Chip size="small" label={`کارمزد ${fa(overview.surcharge.percent)}٪`} variant="outlined" />
            </>
          )}
        </Stack>
      </PageHeader>

      <Paper variant="outlined" sx={{ mb: 2 }}>
        <Tabs value={tab} onChange={(_, v) => { setTab(v); setSearchParams(v ? { tab: String(v) } : {}); }} variant="scrollable" scrollButtons="auto">
          <Tab label="نمای کلی" />
          <Tab label={`سفارش‌ها${overview?.total ? ` (${fa(overview.total)})` : ''}`} />
          <Tab label="لاگ‌ها" />
          <Tab label="ابزارها و تست" />
          <Tab label="راهنما و کدهای خطا" />
        </Tabs>
      </Paper>

      {tab === 0 && renderOverview()}
      {tab === 1 && renderOrders()}
      {tab === 2 && renderLogs()}
      {tab === 3 && renderTools()}
      {tab === 4 && renderGuide()}

      {renderDetailDialog()}
      {renderConfirmDialog()}
    </Box>
  );
};

export default SnappPayCenter;
